import React, { useState } from 'react';
import VersionCard from './VersionCard';

const VersionHistory = ({ 
  versions = [],
  onDownload,
  initialDisplayCount = 5,
  showLoadMore = true
}) => {
  const [displayCount, setDisplayCount] = useState(initialDisplayCount);

  if (!versions || versions.length === 0) {
    return (
      <div style={styles.emptyContainer}>
        <span style={styles.emptyIcon}>📦</span>
        <h3 style={styles.emptyTitle}>No Version History</h3>
        <p style={styles.emptyText}>
          No versions have been uploaded yet.
        </p>
      </div>
    );
  }

  const displayedVersions = versions.slice(0, displayCount);
  const hasMore = versions.length > displayCount;

  const handleLoadMore = () => {
    setDisplayCount(prev => Math.min(prev + initialDisplayCount, versions.length));
  };

  const handleShowLess = () => {
    setDisplayCount(initialDisplayCount);
    // Scroll to top of version history
    if (typeof window !== 'undefined' && window.scrollTo) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>Version History</h2>
        <span style={styles.versionCount}>
          {versions.length} version{versions.length !== 1 ? 's' : ''}
        </span>
      </div>

      <div style={styles.versionList}>
        {displayedVersions.map((version, index) => (
          <VersionCard
            key={version.id || index}
            version={version}
            onDownload={onDownload}
            showDownloadButton={true}
          />
        ))}
      </div>

      {showLoadMore && (hasMore || displayCount > initialDisplayCount) && (
        <div style={styles.loadMoreContainer}>
          {hasMore && (
            <button
              onClick={handleLoadMore}
              style={styles.loadMoreButton}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#2980b9';
                e.target.style.borderColor = '#2980b9';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#3498db';
                e.target.style.borderColor = '#3498db';
              }}
            >
              <span style={styles.buttonIcon}>▼</span>
              View More Versions ({versions.length - displayCount} remaining)
            </button>
          )}
          
          {displayCount > initialDisplayCount && (
            <button
              onClick={handleShowLess}
              style={styles.showLessButton}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#f8f9fa';
                e.target.style.borderColor = '#95a5a6';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#fff';
                e.target.style.borderColor = '#bdc3c7';
              }}
            >
              <span style={styles.buttonIcon}>▲</span>
              Show Less
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    width: '100%',
    marginTop: '24px'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    paddingBottom: '12px',
    borderBottom: '2px solid #ecf0f1'
  },
  title: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#2c3e50',
    margin: 0
  },
  versionCount: {
    backgroundColor: '#3498db',
    color: '#fff',
    padding: '6px 14px',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: '600'
  },
  versionList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  loadMoreContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    alignItems: 'center',
    marginTop: '24px',
    paddingTop: '24px',
    borderTop: '1px solid #ecf0f1'
  },
  loadMoreButton: {
    backgroundColor: '#3498db',
    color: '#fff',
    border: '2px solid #3498db',
    borderRadius: '8px',
    padding: '12px 24px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.2s ease',
    minWidth: '200px',
    justifyContent: 'center'
  },
  showLessButton: {
    backgroundColor: '#fff',
    color: '#7f8c8d',
    border: '2px solid #bdc3c7',
    borderRadius: '8px',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'all 0.2s ease'
  },
  buttonIcon: {
    fontSize: '12px'
  },
  emptyContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '60px 20px',
    backgroundColor: '#f8f9fa',
    borderRadius: '12px',
    textAlign: 'center'
  },
  emptyIcon: {
    fontSize: '64px',
    marginBottom: '16px'
  },
  emptyTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#2c3e50',
    margin: '0 0 8px 0'
  },
  emptyText: {
    fontSize: '14px',
    color: '#7f8c8d',
    margin: 0
  }
};

// Add keyframe animation for spinner
const styleSheet = document.styleSheets[0];
const keyframes = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
try {
  styleSheet.insertRule(keyframes, styleSheet.cssRules.length);
} catch (e) {
  // Ignore if already exists
}

export default VersionHistory;
